import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  ArrowLeft, 
  Download, 
  Send, 
  CheckCircle2, 
  XCircle,
  Truck,
  Calendar,
  User,
  Users,
  FileText,
  Loader2,
  Edit,
  Plus,
  Minus,
  Trash2,
  Save,
  X,
  Search,
  Package,
  History,
  Clock,
  Upload,
  Eye,
  Lock,
  Mail
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';
import { Checkbox } from '../components/ui/checkbox';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const { api, isAdmin, isResponsable, user } = useAuth();
  const { canUseLogo } = useFeatureAccess(); // Pour feature gating PLUS
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [editedNotes, setEditedNotes] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Add product dialog
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);
  
  // Invoice import dialog
  
  // Email selection state for sending order
  const [emailSelectionDialogOpen, setEmailSelectionDialogOpen] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sending, setSending] = useState(false);

  // Function to load order data (reusable)
  const loadOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Commande non trouvée');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
  }, [api, orderId, navigate]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      validated: 'bg-blue-100 text-blue-800 border-blue-200',
      sent: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    const labels = {
      pending: 'En attente',
      validated: 'Validée',
      sent: 'Envoyée',
      rejected: 'Rejetée'
    };
    return (
      <Badge variant="outline" className={`${styles[status]} text-sm px-3 py-1`}>
        {labels[status]}
      </Badge>
    );
  };

  const handleValidate = async () => {
    setActionLoading(true);
    try {
      const response = await api.put(`/orders/${orderId}/validate`);
      setOrder(response.data);
      toast.success('Commande validée');
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      const response = await api.put(`/orders/${orderId}/reject`);
      setOrder(response.data);
      toast.success('Commande rejetée');
    } catch (error) {
      toast.error('Erreur lors du rejet');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSend = async () => {
    if (!order || !order.supplier_id) {
      toast.error('Fournisseur introuvable');
      return;
    }
    
    // Fetch supplier to get all emails
    setActionLoading(true);
    try {
      const response = await api.get(`/suppliers/${order.supplier_id}`);
      const supplier = response.data;
      
      // Build list of available emails
      const emails = [supplier.email];
      if (supplier.secondary_emails && supplier.secondary_emails.length > 0) {
        emails.push(...supplier.secondary_emails);
      }
      
      setAvailableEmails(emails.map((email, index) => ({
        email,
        isPrimary: index === 0,
        supplierName: supplier.name
      })));
      setSelectedEmails([supplier.email]); // Pre-select primary email
      setEmailSelectionDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors de la récupération du fournisseur');
    } finally {
      setActionLoading(false);
    }
  };
  
  const confirmSendOrder = async () => {
    if (selectedEmails.length === 0) {
      toast.error('Veuillez sélectionner au moins un email');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.put(`/orders/${orderId}/send`, {
        recipient_emails: selectedEmails
      });
      setOrder(response.data);
      toast.success(`Commande envoyée à ${selectedEmails.length} email(s)`);
      setEmailSelectionDialogOpen(false);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'envoi'));
    } finally {
      setSending(false);
    }
  };
  
  const toggleEmailSelection = (email) => {
    setSelectedEmails(prev => 
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/orders/${orderId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };


  const handleDeleteOrder = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) return;
    
    setActionLoading(true);
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success('Commande supprimée');
      navigate('/orders');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
    } finally {
      setActionLoading(false);
    }
  };

  // Edit mode functions
  const startEditing = () => {
    setEditedItems(order.items.map(item => ({
      product_id: item.product_id,
      code: item.code,
      article: item.article,
      min_packaging: item.min_packaging,
      unit: item.unit,
      unit_price: item.unit_price,
      quantity: item.quantity
    })));
    setEditedNotes(order.notes || '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditedItems([]);
    setEditedNotes('');
  };

  const updateItemQuantity = (productId, delta) => {
    setEditedItems(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeItem = (productId) => {
    setEditedItems(prev => prev.filter(item => item.product_id !== productId));
  };

  const handleSaveChanges = async () => {
    if (editedItems.length === 0) {
      toast.error('La commande doit contenir au moins un article');
      return;
    }

    setSaveLoading(true);
    try {
      const response = await api.put(`/orders/${orderId}/items`, {
        items: editedItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        notes: editedNotes
      });
      setOrder(response.data);
      setIsEditing(false);
      toast.success('Commande modifiée avec succès');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la modification'));
    } finally {
      setSaveLoading(false);
    }
  };

  // Add product dialog functions
  const openAddProductDialog = async () => {
    setShowAddProduct(true);
    setProductsLoading(true);
    try {
      const response = await api.get(`/products?supplier_id=${order.supplier_id}`);
      // Filter out products already in the order
      const existingIds = editedItems.map(i => i.product_id);
      setAvailableProducts(response.data.filter(p => !existingIds.includes(p.id)));
    } catch (error) {
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setProductsLoading(false);
    }
  };

  const addProduct = (product) => {
    setEditedItems(prev => [...prev, {
      product_id: product.id,
      code: product.code,
      article: product.article,
      min_packaging: product.min_packaging,
      unit: product.unit,
      unit_price: product.unit_price,
      quantity: 1
    }]);
    // Remove from available
    setAvailableProducts(prev => prev.filter(p => p.id !== product.id));
  };

  const filteredProducts = availableProducts.filter(p => 
    p.article.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.code.toLowerCase().includes(productSearch.toLowerCase())
  );

  const calculateEditedTotal = () => {
    return editedItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  };

  // Check if user can edit this order
  const canEdit = order?.status === 'pending' && (
    order?.user_id === user?.id || isAdmin || isResponsable
  );
  
  // Only admins and responsables can delete orders
  const canDelete = isAdmin || isResponsable;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="order-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/orders')}
            className="mb-2 -ml-2 text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux commandes
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{order.order_number}</h1>
            {getStatusBadge(order.status)}
            {isEditing && (
              <Badge className="bg-amber-500 text-white">Mode édition</Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isEditing ? (
            <>
              {(isAdmin || isResponsable) && (
                <>
                  <Button 
                    variant="outline"
                    onClick={handleDownloadPDF}
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                </>
              )}

              {canEdit && (
                <Button 
                  variant="outline"
                  onClick={startEditing}
                  className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  data-testid="edit-order-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              )}

              {canDelete && (
                <Button 
                  variant="outline"
                  onClick={handleDeleteOrder}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                  disabled={actionLoading}
                  data-testid="delete-order-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              )}

              {(isAdmin || isResponsable) && order.status === 'pending' && (
                <>
                  <Button 
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleValidate}
                    disabled={actionLoading}
                    data-testid="validate-order-btn"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Valider
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleReject}
                    disabled={actionLoading}
                    data-testid="reject-order-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeter
                  </Button>
                </>
              )}

              {isAdmin && order.status === 'validated' && (
                <Button 
                  className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                  onClick={handleSend}
                  disabled={actionLoading}
                  data-testid="send-order-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer au fournisseur
                </Button>
              )}
            </>
          ) : (
            <>
              <Button 
                variant="outline"
                onClick={cancelEditing}
                data-testid="cancel-edit-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button 
                className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
                onClick={handleSaveChanges}
                disabled={saveLoading}
                data-testid="save-changes-btn"
              >
                {saveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D8F3DC] flex items-center justify-center">
                <Truck className="w-5 h-5 text-[#1B4332]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Fournisseur</p>
                <p className="font-semibold">{order.supplier_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#F0EBE3] flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#1B4332]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Livraison prévue</p>
                <p className="font-semibold">{format(new Date(order.delivery_date), 'dd MMMM yyyy', { locale: fr })}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Commandé par</p>
                <p className="font-semibold">{order.user_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {order.team_name && (
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Équipe</p>
                  <p className="font-semibold">{order.team_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="border border-gray-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1B4332]" />
              Détail des articles
            </CardTitle>
            {isEditing && (
              <Button 
                size="sm" 
                onClick={openAddProductDialog}
                className="bg-[#1B4332] hover:bg-[#0D2E1F]"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter un produit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Code</TableHead>
                <TableHead className="font-semibold">Article</TableHead>
                <TableHead className="font-semibold">Unité commande</TableHead>
                <TableHead className="text-right font-semibold">Prix/U</TableHead>
                <TableHead className="text-right font-semibold">Qté</TableHead>
                <TableHead className="text-right font-semibold">Total</TableHead>
                {isEditing && <TableHead className="w-[100px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(isEditing ? editedItems : order.items).map((item, index) => (
                <TableRow key={item.product_id || index}>
                  <TableCell className="font-mono text-sm">{item.code}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{item.article}</span>
                      {item.out_of_stock_action && item.out_of_stock_action !== 'replace_better' && (
                        <span className="text-xs text-gray-500 mt-0.5">
                          {item.out_of_stock_action === 'cancel' && '❌ Annuler si rupture'}
                          {item.out_of_stock_action === 'call_first' && '📞 Appeler avant de remplacer'}
                        </span>
                      )}
                      {item.out_of_stock_action === 'replace_better' && (
                        <span className="text-xs text-green-600 mt-0.5">
                          🔄 Remplacement accepté
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-500">
                    {item.catalog_unit
                      || (item.is_divisible && item.divisible_unit)
                      || item.supplier_packaging_type
                      || item.unit
                      || item.order_unit
                      || (item.min_packaging ? `${item.min_packaging}` : '-')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{item.unit_price.toFixed(2)} €</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => updateItemQuantity(item.product_id, -1)}
                          data-testid={`qty-minus-${item.code}`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-7 w-7"
                          onClick={() => updateItemQuantity(item.product_id, 1)}
                          data-testid={`qty-plus-${item.code}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      item.quantity
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {(item.unit_price * item.quantity).toFixed(2)} €
                  </TableCell>
                  {isEditing && (
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeItem(item.product_id)}
                        data-testid={`remove-${item.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              <TableRow className="bg-gray-50">
                <TableCell colSpan={isEditing ? 7 : 6} className="text-right font-bold">Total HT</TableCell>
                <TableCell className="text-right font-bold text-lg tabular-nums">
                  {isEditing ? calculateEditedTotal().toFixed(2) : order.total_amount.toFixed(2)} €
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Custom Products (Non-referenced) */}
      {order.custom_products && order.custom_products.length > 0 && (
        <Card className="border border-gray-200">
          <CardHeader className="pb-3 bg-gray-50 border-b">
            <CardTitle className="text-base flex items-center gap-2 text-gray-700">
              <Package className="w-4 h-4 text-gray-500" />
              Produits hors catalogue ({order.custom_products.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-2">
              {order.custom_products.map((product, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 bg-white rounded border border-gray-200 hover:bg-gray-50 transition-colors"
                  data-testid={`custom-product-display-${index}`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                    <p className="text-xs text-gray-500 mt-1">Quantité : {product.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes section */}
      <Card className="border border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Notes</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Textarea
              value={editedNotes}
              onChange={(e) => setEditedNotes(e.target.value)}
              placeholder="Ajouter des notes à la commande..."
              rows={3}
              data-testid="edit-notes-textarea"
            />
          ) : (
            <p className="text-gray-600">{order.notes || 'Aucune note'}</p>
          )}
        </CardContent>
      </Card>

      {order.validated_by && (
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">
              {order.status === 'validated' || order.status === 'sent' ? 'Validée' : 'Traitée'} par <strong>{order.validated_by}</strong> le {format(new Date(order.validated_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action Log Timeline - Admin only */}
      {(isAdmin || isResponsable) && order.action_log && order.action_log.length > 0 && (
        <Card className="border border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-[#1B4332]" />
              Historique des actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {order.action_log.slice().reverse().map((log, index) => {
                const getActionIcon = (action) => {
                  switch(action) {
                    case 'created': return <Plus className="w-4 h-4" />;
                    case 'validated': return <CheckCircle2 className="w-4 h-4" />;
                    case 'rejected': return <XCircle className="w-4 h-4" />;
                    case 'sent': return <Send className="w-4 h-4" />;
                    default: return <Clock className="w-4 h-4" />;
                  }
                };
                const getActionColor = (action) => {
                  switch(action) {
                    case 'created': return 'bg-blue-100 text-blue-700 border-blue-200';
                    case 'validated': return 'bg-green-100 text-green-700 border-green-200';
                    case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
                    case 'sent': return 'bg-purple-100 text-purple-700 border-purple-200';
                    default: return 'bg-gray-100 text-gray-700 border-gray-200';
                  }
                };
                const getActionLabel = (action) => {
                  switch(action) {
                    case 'created': return 'Création';
                    case 'validated': return 'Validation';
                    case 'rejected': return 'Rejet';
                    case 'sent': return 'Envoi';
                    default: return action;
                  }
                };
                
                return (
                  <div key={`${log.timestamp}-${log.action}-${index}`} className="flex gap-4" data-testid={`action-log-${index}`}>
                    <div className="relative">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                      {index < order.action_log.length - 1 && (
                        <div className="absolute top-8 left-1/2 -translate-x-1/2 w-0.5 h-6 bg-gray-200" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={getActionColor(log.action)}>
                          {getActionLabel(log.action)}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {format(new Date(log.timestamp), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">
                        Par <strong>{log.user}</strong>
                      </p>
                      {log.details && (
                        <p className="text-sm text-gray-500 mt-0.5">{log.details}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Add Product Dialog */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#1B4332]" />
              Ajouter un produit - {order.supplier_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Rechercher un produit..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="pl-10"
              data-testid="product-search-input"
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-[300px] border rounded-lg">
            {productsLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-[#1B4332]" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                {productSearch ? 'Aucun produit trouvé' : 'Tous les produits sont déjà dans la commande'}
              </div>
            ) : (
              <div className="divide-y">
                {filteredProducts.map((product) => (
                  <div 
                    key={product.id}
                    className="p-3 flex items-center justify-between hover:bg-gray-50"
                  >
                    <div>
                      <p className="font-medium">{product.article}</p>
                      <p className="text-sm text-gray-500">
                        {product.code} • {product.min_packaging} • {product.unit_price.toFixed(2)} €/{product.unit}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => addProduct(product)}
                      className="bg-[#1B4332] hover:bg-[#0D2E1F]"
                      data-testid={`add-product-${product.code}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProduct(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Invoice Upload Modal */}
      {/* TODO: Créer le composant SupplierInvoiceUploadModal si nécessaire
      <SupplierInvoiceUploadModal
        isOpen={showInvoiceUpload}
        onClose={() => setShowInvoiceUpload(false)}
        orderId={orderId}
        onSuccess={handleInvoiceUploadSuccess}
      />
      */}

      {/* Email Selection Dialog for Sending Order */}
      <Dialog open={emailSelectionDialogOpen} onOpenChange={setEmailSelectionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1B4332]" />
              Sélectionner les destinataires
            </DialogTitle>
            <DialogDescription>
              Choisissez les emails qui recevront cette commande pour{' '}
              <strong>{order.supplier_name}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {availableEmails.map((emailObj, index) => (
              <div
                key={emailObj.email}
                onClick={() => toggleEmailSelection(emailObj.email)}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <Checkbox
                  checked={selectedEmails.includes(emailObj.email)}
                  onCheckedChange={() => toggleEmailSelection(emailObj.email)}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{emailObj.email}</span>
                  </div>
                  {emailObj.isPrimary && (
                    <Badge className="mt-1 text-xs bg-[#1B4332]">Principal</Badge>
                  )}
                  {!emailObj.isPrimary && (
                    <span className="text-xs text-gray-500">Email secondaire {index}</span>
                  )}
                </div>
              </div>
            ))}
            
            {selectedEmails.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>{selectedEmails.length}</strong> email(s) sélectionné(s)
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEmailSelectionDialogOpen(false)}
              disabled={sending}
            >
              Annuler
            </Button>
            <Button
              onClick={confirmSendOrder}
              disabled={sending || selectedEmails.length === 0}
              className="bg-[#1B4332] hover:bg-[#0D2E1F]"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer la commande
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default OrderDetailPage;
